/**
 * A non-ride (run, swim, gym...) in the rides list and calendar
 * (docs/ACTIVITIES_PLAN.md §4): sport icon, name, duration, distance
 * and heart rate. No power, TSS or ride-type badges.
 */
import { useTranslation } from 'react-i18next';
import { RideAttribution } from './GarminAttribution.tsx';
import { ProviderIcon } from './ProviderIcon.tsx';
import {
  formatDistanceKm,
  formatDuration,
  formatRideDateShort,
} from '../lib/rideFormat.ts';
import { activityLine, showsDistance, sportIcon, sportNameKey } from '../lib/sports.ts';
import type { RideListItem } from '../types/api.ts';

/** Card in the rides list. */
export function ActivityCard({
  item,
  onClick,
}: Readonly<{ item: RideListItem; onClick: () => void }>) {
  const { t } = useTranslation();
  const sportName = t(sportNameKey(item.sport));
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="activity-card"
      className="w-full text-left rounded-xl bg-(--color-bg-secondary) border border-(--color-border) hover:border-(--color-accent)/50 transition-colors p-4 flex flex-col gap-2"
    >
      <div className="flex items-center gap-2 text-(--color-text-secondary) text-xs">
        <span aria-hidden="true">{sportIcon(item)}</span>
        <span>{sportName}</span>
        <span>·</span>
        <span>{formatRideDateShort(item.started_at)}</span>
        <span className="ml-auto"><ProviderIcon provider={item.provider} size={14} /></span>
      </div>
      <h3 className="text-base font-semibold text-(--color-text-primary) truncate">
        {item.name ?? sportName}
      </h3>
      <RideAttribution text={item.attribution} />
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
        <ActivityStat label={t('dashboard.statDuration')} value={formatDuration(item.duration_sec)} />
        {showsDistance(item) && (
          <ActivityStat label={t('dashboard.statDistance')} value={formatDistanceKm(item.distance_m)} />
        )}
        {item.avg_hr != null && (
          <ActivityStat label={t('dashboard.statAvgHr')} value={`${item.avg_hr} bpm`} />
        )}
      </div>
    </button>
  );
}

function ActivityStat({ label, value }: Readonly<{ label: string; value: string }>) {
  return (
    <div>
      <div className="text-[11px] text-(--color-text-secondary) uppercase tracking-wide">{label}</div>
      <div className="text-(--color-text-primary) font-medium">{value}</div>
    </div>
  );
}

/** Small muted dot in a desktop calendar cell; grey, unlike the colored ride badges. */
export function ActivityDot({
  item,
  onClick,
}: Readonly<{ item: RideListItem; onClick: () => void }>) {
  const { t } = useTranslation();
  const label = `${item.name ?? t(sportNameKey(item.sport))} — ${activityLine(item, t(sportNameKey(item.sport)))}`;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      data-testid="activity-dot"
      className="inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-(--color-bg-tertiary)"
    >
      <span className="block w-2 h-2 rounded-full bg-gray-400/70" />
    </button>
  );
}

/** One muted line in the phone day row: dot, icon, name, duration... */
export function ActivityLineButton({
  item,
  onClick,
}: Readonly<{ item: RideListItem; onClick: () => void }>) {
  const { t } = useTranslation();
  const sportName = t(sportNameKey(item.sport));
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="activity-line"
      className="flex items-center gap-2 w-full text-left px-2 py-1 rounded text-xs text-(--color-text-secondary) hover:bg-(--color-bg-tertiary)"
    >
      <span className="shrink-0 w-2 h-2 rounded-full bg-gray-400/70" aria-hidden="true" />
      <span aria-hidden="true">{sportIcon(item)}</span>
      <span className="truncate">
        {item.name ? `${item.name} · ` : ''}
        {activityLine(item, sportName)}
      </span>
    </button>
  );
}
